import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList } from 'recharts'
import { getKpisOt } from '../api/client'
import PageHeader from '../components/PageHeader'
import Periodo from '../components/ot/Periodo'
import { rangoDe } from '../components/ot/rangos'
import { formatFecha } from '../components/unidades/tipos'

// Operaciones > Órdenes de trabajo > Indicadores: the module's standard KPIs
// (GET /api/ot/kpis). Flow ones (created, closed, lead time, on time) for
// the period; stock ones (open, overdue, aging, SAP queue) as of now.

// Validated pair (light surface): created (blue) / closed (orange); one series in blue.
const AZUL = '#2a78d6'
const NARANJO = '#eb6834'
const num = (v, d = 1) => (v == null ? '—' : Number(v).toLocaleString('es-CL', { maximumFractionDigits: d }))

export default function PlantaIndicadores() {
  const { t } = useTranslation(['operations', 'common'])
  const [rango, setRango] = useState(() => rangoDe('90'))
  const [tipo, setTipo] = useState('')
  const [k, setK] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let vivo = true
    getKpisOt({ ...rango, ...(tipo ? { tipo } : {}) })
      .then(d => { if (vivo) { setK(d); setError('') } })
      .catch(err => { if (vivo) setError(err.response?.status === 403 ? t('ot.kpis.sinFuncion') : t('ot.kpis.cargaFallo')) })
    return () => { vivo = false }
  }, [rango, tipo, t])

  const etapas = (k?.abiertas_por_estado || []).map(e => ({ ...e, nombre: t(`ot.estados.${e.estado}`, { defaultValue: e.estado }) }))
  const semanas = (k?.semanas || []).map(s => ({ ...s, etiqueta: formatFecha(s.semana).slice(0, 5) }))
  const cumple = k?.cumplimiento_compromiso

  return (
    <div className="p-8">
      <div className="mb-6"><PageHeader title={t('ot.kpis.titulo')} subtitle={t('ot.kpis.subtitulo')} /></div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Periodo valor={rango} onChange={setRango} inicial="90" />
        <select value={tipo} onChange={e => setTipo(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('ot.tipoFiltro.todos')}</option>
          <option value="fabricacion">{t('ot.tipoFiltro.fabricacion')}</option>
          <option value="mantenimiento">{t('ot.tipoFiltro.mantenimiento')}</option>
          <option value="servicio">{t('ot.tipoFiltro.servicio')}</option>
        </select>
        <span className="text-xs text-gray-400">{formatFecha(rango.desde)} – {formatFecha(rango.hasta)}</span>
      </div>
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.kpis.enElPeriodo')}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <Tile etiqueta={t('ot.kpis.creadas')} valor={k ? k.creadas : '…'} />
        <Tile etiqueta={t('ot.kpis.cerradas')} valor={k ? k.cerradas : '…'} nota={k?.canceladas ? t('ot.kpis.canceladas', { count: k.canceladas }) : null} />
        <Tile etiqueta={t('ot.kpis.leadTime')} valor={k ? `${num(k.lead_time_dias.promedio)} d` : '…'}
          nota={k?.lead_time_dias.mediana != null ? t('ot.kpis.mediana', { v: num(k.lead_time_dias.mediana) }) : t('ot.kpis.leadTimeNota')} />
        <Tile etiqueta={t('ot.kpis.ciclo')} valor={k ? `${num(k.ciclo_ejecucion_dias.promedio)} d` : '…'} nota={t('ot.kpis.cicloNota')} />
        <Tile etiqueta={t('ot.kpis.cumplimiento')} valor={k ? (cumple.pct == null ? '—' : `${num(cumple.pct)} %`) : '…'}
          nota={k ? t('ot.kpis.cumplimientoNota', { a: cumple.a_tiempo, de: cumple.con_fecha }) : null}
          tono={cumple?.pct == null ? null : cumple.pct >= 90 ? 'bien' : cumple.pct >= 70 ? 'ojo' : 'mal'} />
        <Tile etiqueta={t('ot.kpis.horas')} valor={k ? `${num(k.horas.total)} h` : '…'}
          nota={k?.horas.por_tipo.length ? k.horas.por_tipo.map(h => `${t(`ot.tipos.${h.tipo}`, { defaultValue: h.tipo })} ${num(h.horas)} h`).join(' · ') : null} />
      </div>

      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.kpis.hoy')}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        <Tile etiqueta={t('ot.kpis.abiertas')} valor={k ? k.abiertas : '…'} />
        <Tile etiqueta={t('ot.kpis.vencidas')} valor={k ? k.vencidas : '…'} nota={t('ot.kpis.vencidasNota')} tono={k?.vencidas ? 'mal' : 'bien'} />
        <Tile etiqueta={t('ot.kpis.antiguedad')} valor={k ? `${num(k.antiguedad_abiertas_dias.promedio)} d` : '…'}
          nota={k ? t('ot.kpis.mas90', { count: k.antiguedad_abiertas_dias.mas_de_90 }) : null} tono={k?.antiguedad_abiertas_dias.mas_de_90 ? 'ojo' : null} />
        <Tile etiqueta={t('ot.kpis.planificacion')} valor={k ? (k.planificacion_completa.pct == null ? '—' : `${num(k.planificacion_completa.pct)} %`) : '…'}
          nota={k ? t('ot.kpis.planificacionNota', { a: k.planificacion_completa.completas, de: k.planificacion_completa.total }) : null}
          tono={k?.planificacion_completa.pct == null ? null : k.planificacion_completa.pct >= 100 ? 'bien' : 'ojo'} />
        <Tile etiqueta={t('ot.kpis.devoluciones')} valor={k ? (k.devoluciones.pct == null ? '—' : `${num(k.devoluciones.pct)} %`) : '…'}
          nota={k ? t('ot.kpis.devolucionesNota', { count: k.devoluciones.n }) : null} />
        <Tile etiqueta={t('ot.kpis.colaSap')} valor={k ? k.cola_sap.pendientes : '…'}
          nota={k ? [k.cola_sap.antiguedad_promedio_dias != null && t('ot.kpis.colaSapEdad', { dias: num(k.cola_sap.antiguedad_promedio_dias) }),
            k.cola_sap.rechazadas > 0 && t('ot.kpis.colaSapRech', { count: k.cola_sap.rechazadas })].filter(Boolean).join(' · ') || null : null}
          tono={k?.cola_sap.rechazadas ? 'mal' : null} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('ot.kpis.porEtapa')}</h3>
          <p className="text-xs text-gray-500 mb-3">{t('ot.kpis.porEtapaNota')}</p>
          {!etapas.length ? <p className="text-sm text-gray-400 py-10 text-center">{t('ot.kpis.sinDatos')}</p> : (
            <div style={{ height: Math.max(160, etapas.length * 36) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={etapas} layout="vertical" margin={{ top: 0, right: 36, left: 8, bottom: 0 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 12, fill: '#374151' }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: '#f3f4f6' }} formatter={(v) => [v, t('ot.kpis.ots')]} />
                  <Bar dataKey="n" fill={AZUL} radius={[0, 4, 4, 0]} maxBarSize={22}>
                    <LabelList dataKey="n" position="right" style={{ fontSize: 12, fill: '#374151' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('ot.kpis.porSemana')}</h3>
          <p className="text-xs text-gray-500 mb-3">{t('ot.kpis.porSemanaNota')}</p>
          {!semanas.length ? <p className="text-sm text-gray-400 py-10 text-center">{t('ot.kpis.sinDatos')}</p> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={semanas} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barGap={2}>
                  <CartesianGrid vertical={false} stroke="#eceae4" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#d6d3cc' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: '#f3f4f6' }} labelFormatter={(_, pl) => (pl?.[0] ? t('ot.kpis.semanaDel', { fecha: formatFecha(pl[0].payload.semana) }) : '')} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: '#374151' }}>{v}</span>} />
                  <Bar dataKey="creadas" name={t('ot.kpis.creadas')} fill={AZUL} radius={[4, 4, 0, 0]} maxBarSize={18} />
                  <Bar dataKey="cerradas" name={t('ot.kpis.cerradas')} fill={NARANJO} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      {k?.abiertas_por_origen?.length > 0 && (
        <section className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('ot.kpis.porOrigen')}</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            {k.abiertas_por_origen.map(o => (
              <span key={o.origen} className="text-gray-700">{t(`ot.origenes.${o.origen}`, { defaultValue: o.origen })}: <b className="tabular-nums">{o.n}</b></span>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

// A KPI tile. tono marks good / watch / bad with a word and an icon, never color alone.
function Tile({ etiqueta, valor, nota, tono }) {
  const { t } = useTranslation('operations')
  const T = { bien: ['✓', 'text-emerald-700'], ojo: ['!', 'text-amber-700'], mal: ['▲', 'text-rose-700'] }[tono]
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{etiqueta}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{valor}</p>
      {T && <p className={`text-[11px] font-medium ${T[1]}`}>{T[0]} {t(`ot.kpis.tono.${tono}`)}</p>}
      {nota && <p className="text-[11px] text-gray-500">{nota}</p>}
    </div>
  )
}
